import type { Disposable } from '../../types/disposable';
import type { EventHandler, EventEmitter } from '../event-emitter';

/**
 * 事件发射器实现类
 */
export class EventEmitterImpl<Events extends Record<string, any>> implements EventEmitter<Events> {
    private handlers: Map<keyof Events, Set<EventHandler>> = new Map();

    emit<E extends keyof Events>(
        event: E,
        ...args: Events[E] extends void ? [] : [Events[E]]
    ): void {
        const handlers = this.handlers.get(event);
        if (handlers) {
            const eventData = args.length > 0 ? args[0] : undefined;
            handlers.forEach(handler => handler(eventData));
        }
    }

    on<E extends keyof Events>(event: E, handler: EventHandler<Events[E]>): Disposable {
        if (!this.handlers.has(event)) {
            this.handlers.set(event, new Set());
        }
        this.handlers.get(event)!.add(handler);

        return {
            dispose: () => {
                this.off(event, handler);
            },
        };
    }

    once<E extends keyof Events>(event: E, handler: EventHandler<Events[E]>): Disposable {
        const onceHandler: EventHandler<Events[E]> = (args: Events[E]) => {
            handler(args);
            this.off(event, onceHandler);
        };

        return this.on(event, onceHandler);
    }

    off<E extends keyof Events>(event: E, handler: EventHandler<Events[E]>): void {
        const handlers = this.handlers.get(event);
        if (handlers) {
            handlers.delete(handler);
            if (handlers.size === 0) {
                this.handlers.delete(event);
            }
        }
    }

    clear(event?: keyof Events): void {
        if (event) {
            this.handlers.delete(event);
        } else {
            this.handlers.clear();
        }
    }
}
